import { useEffect, useRef, useState } from "react";
import { Monitor, Smartphone, Tablet } from "lucide-react";
import "./ResponsivePreviewSection.css";

/** Static hero screenshots for all device tabs. */
const STATIC_PREVIEW_SRC = {
  desktop: "/previews/desktop-preview.png",
  tablet: "/previews/tablet-preview.png",
  mobile: "/previews/mobile-preview.png",
};

const DEVICES = [
  {
    id: "desktop",
    shortLabel: "Desktop",
    icon: Monitor,
    width: 1440,
    height: 720,
    chrome: 28,
    shellPad: 0,
  },
  {
    id: "tablet",
    shortLabel: "Tablet",
    icon: Tablet,
    width: 768,
    height: 900,
    chrome: 0,
    shellPad: 14,
  },
  {
    id: "mobile",
    shortLabel: "Mobile",
    icon: Smartphone,
    width: 375,
    height: 667,
    chrome: 0,
    shellPad: 10,
  },
];

const MAX_DISPLAY = {
  desktop: { w: 380, h: 230 },
  tablet: { w: 250, h: 300 },
  mobile: { w: 170, h: 300 },
};

const MAX_DISPLAY_INLINE = {
  desktop: { w: 328, h: 198 },
  tablet: { w: 218, h: 262 },
  mobile: { w: 148, h: 262 },
};

const MAX_DISPLAY_PORTFOLIO_DETAIL = {
  desktop: { w: 360, h: 220 },
  tablet: { w: 240, h: 290 },
  mobile: { w: 168, h: 290 },
};

function computeLayout(device, containerWidth, variant = "default") {
  const isCompact =
    variant === "hero-inline" || variant === "portfolio-detail";
  const base =
    variant === "portfolio-detail"
      ? MAX_DISPLAY_PORTFOLIO_DETAIL[device.id] || MAX_DISPLAY[device.id]
      : isCompact
        ? MAX_DISPLAY_INLINE[device.id] || MAX_DISPLAY[device.id]
        : MAX_DISPLAY[device.id];
  const limits =
    isCompact && device.id === "mobile" ? { w: base.w, h: 280 } : base;
  const pad = 24;
  const maxW = Math.min(Math.max(containerWidth - pad, 120), limits.w);
  const maxH = limits.h;
  const shellX = device.shellPad * 2;
  const scale = Math.min(
    (maxW - shellX) / device.width,
    (maxH - device.chrome - shellX) / device.height,
    1,
  );

  const displayW = Math.round(device.width * scale);
  const displayH = Math.round(device.height * scale);
  const shellW = displayW + shellX;
  const shellH = displayH + shellX + device.chrome;

  return { scale, displayW, displayH, shellW, shellH, stageW: shellW + 16, stageH: shellH + 16 };
}

export default function ResponsivePreviewSection({ variant = "default" }) {
  const isInline =
    variant === "hero-inline" || variant === "portfolio-detail";
  const hideHead = variant === "portfolio-detail";
  const [deviceId, setDeviceId] = useState("desktop");
  const [layout, setLayout] = useState(() =>
    computeLayout(DEVICES[0], 360, variant),
  );
  const [previewReady, setPreviewReady] = useState(false);
  const rootRef = useRef(null);

  const device = DEVICES.find((d) => d.id === deviceId) || DEVICES[0];

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    let cancelled = false;
    let timerId = null;
    let scheduled = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || cancelled || scheduled) return;
        scheduled = true;
        timerId = window.setTimeout(() => {
          if (!cancelled) setPreviewReady(true);
        }, 400);
      },
      { rootMargin: "40px" },
    );
    observer.observe(root);

    return () => {
      cancelled = true;
      observer.disconnect();
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const update = () => {
      setLayout(computeLayout(device, root.clientWidth || 360, variant));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(root);
    return () => observer.disconnect();
  }, [device, variant]);

  const { displayW, displayH, shellW, shellH, stageW, stageH } = layout;

  return (
    <div
      ref={rootRef}
      className={`ds-rp${isInline ? " ds-rp--inline" : ""}${hideHead ? " ds-rp--no-head" : ""}`}
      data-testid="responsive-preview"
    >
      <div className="ds-rp-switcher" role="tablist" aria-label="화면 크기 선택">
        {DEVICES.map(({ id, shortLabel, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={deviceId === id}
            aria-label={shortLabel}
            data-testid={`preview-device-${id}`}
            className={`ds-rp-device-btn${deviceId === id ? " is-active" : ""}`}
            onClick={() => setDeviceId(id)}
          >
            <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
            <span className="ds-rp-device-label">{shortLabel}</span>
          </button>
        ))}
      </div>

      {!hideHead ? (
        <div className="ds-rp-head ds-rp-head--inline">
          <p className="ds-rp-title">모든 화면에서 완벽하게</p>
          <p className="ds-rp-desc">
            {device.width} × {device.height}
          </p>
        </div>
      ) : null}

      <div
        className="ds-rp-stage"
        style={{ width: stageW, height: stageH, maxWidth: "100%" }}
      >
        <div
          className={`ds-rp-shell ds-rp-shell--${device.id}`}
          style={{ width: shellW, height: shellH }}
        >
          {device.id === "desktop" ? (
            <div className="ds-rp-browser-bar" aria-hidden="true">
              <span className="ds-rp-browser-dot" />
              <span className="ds-rp-browser-dot" />
              <span className="ds-rp-browser-dot" />
            </div>
          ) : null}

          {device.id === "tablet" ? (
            <div className="ds-rp-notch ds-rp-notch--tablet" aria-hidden="true" />
          ) : null}

          {device.id === "mobile" ? (
            <div className="ds-rp-notch ds-rp-notch--mobile" aria-hidden="true" />
          ) : null}

          <div className="ds-rp-clip" style={{ width: displayW, height: displayH }}>
            {!previewReady ? (
              <div className="ds-rp-loading" aria-hidden="true">
                <span />
              </div>
            ) : null}
            {STATIC_PREVIEW_SRC[device.id] ? (
              <img
                src={STATIC_PREVIEW_SRC[device.id]}
                alt=""
                aria-hidden="true"
                data-testid={`${device.id}-preview-shot`}
                className={`ds-rp-device-shot ds-rp-device-shot--${device.id}`}
                decoding="async"
                loading="lazy"
                fetchPriority="low"
                draggable={false}
                style={{
                  visibility: previewReady ? "visible" : "hidden",
                }}
              />
            ) : null}
          </div>

          {device.id === "mobile" ? (
            <div className="ds-rp-home-bar" aria-hidden="true" />
          ) : null}
        </div>
      </div>
    </div>
  );
}
